import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGrid, type ColumnDef } from '@/components/DataGrid';
import { Icon } from '@/components/Icon';
import { Status } from '@/components/Status';
import { useAudit, useDLQ } from '@/hooks/useAdmin';
import { useAttributeDefs } from '@/hooks/useAttributeDefs';
import { fmt, formatDateTime, sourceLabel } from '@/lib/format';
import { AdminDashboards } from './AdminDashboards';
import type { AttributeDef } from '@/api/attributeDefs';
import type { AuditItem, DLQItem } from '@/api/admin';

type View = 'dashboards' | 'dlq' | 'audit' | 'dict';

/**
 * Admin module — four sub-views accessible from a single pill nav.
 * Each sub-view uses the shared DataGrid so sorting / filtering /
 * pagination behaves consistently across the back-office.
 */
export function AdminPage() {
  const { t } = useTranslation();
  const [view, setView] = useState<View>('dashboards');
  return (
    <div className="fade-in">
      <div className="page-head">
        <div>
          <div className="pt">{t('pages.admin.title')}</div>
          <div className="ps">{t('pages.admin.subtitle')}</div>
        </div>
      </div>

      <div className="sub-nav" style={{ maxWidth: 640 }}>
        <button className={view === 'dashboards' ? 'on' : ''} onClick={() => setView('dashboards')}>
          <Icon name="chart" />
          Tableaux de bord
        </button>
        <button className={view === 'dlq' ? 'on' : ''} onClick={() => setView('dlq')}>
          <Icon name="inbox" />
          File de rejets
        </button>
        <button className={view === 'audit' ? 'on' : ''} onClick={() => setView('audit')}>
          <Icon name="history" />
          Journal d'audit
        </button>
        <button className={view === 'dict' ? 'on' : ''} onClick={() => setView('dict')}>
          <Icon name="layers" />
          Dictionnaire
        </button>
      </div>

      {view === 'dashboards' && <AdminDashboards />}
      {view === 'dlq' && <DLQView />}
      {view === 'audit' && <AuditView />}
      {view === 'dict' && <DictionaryView />}
    </div>
  );
}

function DLQView() {
  const [code, setCode] = useState('');
  const [source, setSource] = useState('');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });

  const query = useDLQ({
    code: code || undefined,
    source: source || undefined,
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
  });
  const rows = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  const columns = useMemo<ColumnDef<DLQItem>[]>(
    () => [
      {
        accessorKey: 'code',
        header: 'Code',
        size: 200,
        Cell: ({ cell }) => <span className="code">{cell.getValue<string>()}</span>,
      },
      {
        accessorFn: (r) => sourceLabel(r.source),
        id: 'source',
        header: 'Source',
        size: 130,
      },
      {
        accessorFn: (r) => r.subjectPhone ?? r.subjectActor ?? '',
        id: 'subject',
        header: 'Sujet',
        size: 180,
        Cell: ({ cell }) => (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
            {cell.getValue<string>() || '—'}
          </span>
        ),
      },
      {
        accessorFn: (r) => r.attributeType ?? '',
        id: 'attributeType',
        header: 'Type',
        size: 170,
        Cell: ({ cell }) => cell.getValue<string>() || '—',
      },
      {
        accessorKey: 'message',
        header: 'Message',
        size: 360,
        Cell: ({ cell }) => (
          <span className="muted" style={{ fontSize: 12.5 }}>
            {cell.getValue<string>() ?? ''}
          </span>
        ),
      },
      {
        accessorKey: 'recordedAt',
        header: 'Reçu le',
        size: 170,
        Cell: ({ cell }) => (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink-3)' }}>
            {formatDateTime(cell.getValue<string>())}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div>
      <div className="toolbar">
        <label className="input">
          <Icon name="layers" />
          <select value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="">Toutes les sources</option>
            <option value="ORANGE">Orange</option>
            <option value="ID30">PDE.CI</option>
            <option value="CALL_CENTER">Call center</option>
            <option value="MANUAL">Manuel</option>
            <option value="BACK_OFFICE">Back-office</option>
          </select>
        </label>
        <label className="input">
          <Icon name="filter" />
          <input
            placeholder="Code (ex INVALID_SUBJECT)"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </label>
        <div className="muted" style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 600 }}>
          {fmt(total)} rejets au total
        </div>
      </div>

      <DataGrid
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        pagination={pagination}
        onPaginationChange={setPagination}
        rowCount={total}
        emptyMessage="DLQ vide. Aucun rejet en attente."
      />
    </div>
  );
}

function AuditView() {
  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });

  const query = useAudit({
    action: action || undefined,
    actor: actor || undefined,
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
  });
  const rows = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  const columns = useMemo<ColumnDef<AuditItem>[]>(
    () => [
      {
        accessorKey: 'action',
        header: 'Action',
        size: 220,
        Cell: ({ cell }) => <span className="code">{cell.getValue<string>()}</span>,
      },
      {
        accessorKey: 'actorLabel',
        header: 'Acteur',
        size: 200,
        Cell: ({ cell }) => (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{cell.getValue<string>()}</span>
        ),
      },
      {
        accessorFn: (r) => (r.targetKind && r.targetId ? `${r.targetKind}/${r.targetId}` : ''),
        id: 'target',
        header: 'Cible',
        size: 220,
        Cell: ({ cell }) => (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
            {cell.getValue<string>() || '—'}
          </span>
        ),
      },
      {
        accessorFn: (r) => (r.metadata != null ? JSON.stringify(r.metadata) : ''),
        id: 'metadata',
        header: 'Métadonnées',
        size: 320,
        Cell: ({ cell }) => (
          <span className="muted" style={{ fontSize: 12 }}>
            {cell.getValue<string>().slice(0, 140) || '—'}
          </span>
        ),
      },
      {
        accessorKey: 'occurredAt',
        header: 'Quand',
        size: 170,
        Cell: ({ cell }) => (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink-3)' }}>
            {formatDateTime(cell.getValue<string>())}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div>
      <div className="toolbar">
        <label className="input">
          <Icon name="activity" />
          <input
            placeholder="Action (ex auth.login.success)"
            value={action}
            onChange={(e) => setAction(e.target.value)}
          />
        </label>
        <label className="input">
          <Icon name="user" />
          <input
            placeholder="Acteur (email)"
            value={actor}
            onChange={(e) => setActor(e.target.value)}
          />
        </label>
        <div className="muted" style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 600 }}>
          {fmt(total)} événements
        </div>
      </div>

      <DataGrid
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        pagination={pagination}
        onPaginationChange={setPagination}
        rowCount={total}
        emptyMessage="Aucune action sensible enregistrée pour ces filtres."
      />
    </div>
  );
}

function DictionaryView() {
  const query = useAttributeDefs();
  const items = query.data ?? [];

  const columns = useMemo<ColumnDef<AttributeDef>[]>(
    () => [
      {
        accessorKey: 'code',
        header: 'Code',
        size: 200,
        Cell: ({ cell }) => <span className="code">{cell.getValue<string>()}</span>,
      },
      {
        accessorKey: 'label',
        header: 'Label',
        size: 260,
      },
      {
        accessorKey: 'family',
        header: 'Famille',
        size: 160,
      },
      {
        accessorKey: 'subjectType',
        header: 'Sujet',
        size: 130,
        Cell: ({ cell }) => (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{cell.getValue<string>()}</span>
        ),
      },
      {
        accessorFn: (d) => (d.allowedSources ?? []).join(', '),
        id: 'sources',
        header: 'Sources autorisées',
        size: 260,
        Cell: ({ row }) => (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(row.original.allowedSources ?? []).map((s) => (
              <span key={s} className="ref">{s}</span>
            ))}
          </div>
        ),
      },
      {
        accessorKey: 'schemaVersion',
        header: 'Schéma',
        size: 100,
        Cell: ({ cell }) => (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink-3)' }}>
            v{cell.getValue<number>()}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Statut',
        size: 110,
        Cell: ({ cell }) => (
          <Status kind={cell.getValue<string>() === 'ACTIVE' ? 'ok' : 'mute'}>
            {cell.getValue<string>()}
          </Status>
        ),
      },
    ],
    [],
  );

  return (
    <div>
      <div className="toolbar">
        <div className="muted" style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 600 }}>
          {items.length} définition{items.length > 1 ? 's' : ''} active{items.length > 1 ? 's' : ''}
        </div>
      </div>

      <DataGrid
        columns={columns}
        data={items}
        isLoading={query.isLoading}
        emptyMessage="Aucune définition d'attribut enregistrée."
      />

      <div className="muted" style={{ marginTop: 14, fontSize: 12.5, fontWeight: 500 }}>
        La création et la mise à jour du dictionnaire seront disponibles prochainement.
      </div>
    </div>
  );
}
